import create from 'zustand/vanilla';

/**
 * 프로필링크 편집기(리뉴얼 셸)의 화면 상태.
 *
 * 블록 데이터 자체는 기존대로 spaceUserV2Store 가 단일 소스이고, 지금 고른 블록도
 * 기존 `active_block` 을 그대로 쓴다 — 미리보기 스크롤 동기화가 그 값에 걸려 있어
 * 별도 선택 상태를 만들면 둘이 어긋난다.
 *
 * 이 스토어는 "어느 화면을 보고 있는가"만 들고 있다.
 * (좁은 화면에서 설정판/미리보기를 덮었는지, 나라별 표에서 어느 칸을 고치는 중인지 등)
 */

/** 위쪽 큰 탭 */
export type ProfileLinkEditorScreen = 'blocks' | 'locales';
/** 설정판 안쪽 탭 */
export type ProfileLinkInspectorTab = 'content' | 'locale';
/** 나라별 표의 보기 필터 */
export type ProfileLinkMatrixFilter = 'empty' | 'all' | 'gonggu';

/** 나라별 표에서 지금 고치고 있는 칸 */
export interface ProfileLinkEditingCell {
    blockId: string;
    lang: string;
}

interface ProfileLinkEditorState {
    screen: ProfileLinkEditorScreen;
    inspectorTab: ProfileLinkInspectorTab;
    /** 좁은 화면에서 설정판이 목록을 덮고 있는지 (넓은 화면에선 항상 보이므로 무시된다) */
    inspectorOpen: boolean;
    /** 좁은 화면에서 미리보기를 전체로 열었는지 */
    previewOpen: boolean;
    matrixFilter: ProfileLinkMatrixFilter;
    editingCell: ProfileLinkEditingCell | null;
    /**
     * "미리보기에서 보기" 를 누른 횟수. 값이 바뀔 때마다 미리보기가 고른 블록으로 스크롤하고 흔들린다.
     * 이미 고른 블록을 다시 눌러도 신호가 가도록 불리언이 아니라 카운터로 둔다.
     */
    previewFocus: number;
}

export interface ProfileLinkEditorStore extends ProfileLinkEditorState {
    setScreen: (screen: ProfileLinkEditorScreen) => void;
    setInspectorTab: (tab: ProfileLinkInspectorTab) => void;
    openInspector: () => void;
    closeInspector: () => void;
    setPreviewOpen: (open: boolean) => void;
    setMatrixFilter: (filter: ProfileLinkMatrixFilter) => void;
    setEditingCell: (cell: ProfileLinkEditingCell | null) => void;
    /** 고른 블록을 미리보기에서 찾아 보여 달라는 신호 */
    focusPreview: () => void;
    reset: () => void;
}

const initializeState: ProfileLinkEditorState = {
    screen: 'blocks',
    inspectorTab: 'content',
    inspectorOpen: false,
    previewOpen: false,
    matrixFilter: 'empty',
    editingCell: null,
    previewFocus: 0,
};

export const profileLinkEditorStore = create<ProfileLinkEditorStore>()((set) => ({
    ...initializeState,

    // 화면을 옮기면 고치던 칸은 놓는다 — 표를 떠난 뒤에도 남아 있으면 다시 들어왔을 때
    // 엉뚱한 칸이 열려 있다.
    setScreen: (screen) => set({ screen, editingCell: null }),

    setInspectorTab: (inspectorTab) => set({ inspectorTab }),

    openInspector: () => set({ inspectorOpen: true }),

    closeInspector: () => set({ inspectorOpen: false }),

    setPreviewOpen: (previewOpen) => set({ previewOpen }),

    setMatrixFilter: (matrixFilter) => set({ matrixFilter, editingCell: null }),

    setEditingCell: (editingCell) => set({ editingCell }),

    focusPreview: () => set((state) => ({ previewFocus: state.previewFocus + 1 })),

    reset: () => set(initializeState),
}));

export default profileLinkEditorStore;
